import { useCallback, useState } from 'react';
import { Upload, Loader2, FileArchive, FileText, X } from 'lucide-react';

interface StagingUploaderProps {
  onUpload: (files: File[]) => Promise<void>;
  uploading: boolean;
}

export function StagingUploader({ onUpload, uploading }: StagingUploaderProps) {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const filterFiles = (files: FileList | File[]): File[] => {
    return Array.from(files).filter(f =>
      f.type === 'application/pdf' ||
      f.type === 'text/plain' ||
      f.type === 'application/zip' ||
      f.type === 'application/x-zip-compressed' ||
      f.name.endsWith('.md') ||
      f.name.endsWith('.zip')
    );
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = filterFiles(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...droppedFiles]);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = filterFiles(e.target.files);
      setSelectedFiles(prev => [...prev, ...selected]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || uploading) return;
    await onUpload(selectedFiles);
    setSelectedFiles([]);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (file: File) => {
    if (file.name.endsWith('.zip') || file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
      return <FileArchive size={16} className="text-orange-500 dark:text-orange-400" />;
    }
    return <FileText size={16} className="text-blue-500 dark:text-blue-400" />;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`p-8 text-center transition-colors ${
          dragOver
            ? 'bg-primary-50 border-primary-400 dark:bg-primary-900/20'
            : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-700/50'
        }`}
      >
        <Upload size={40} className={`mx-auto mb-3 ${dragOver ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
        <p className="text-gray-700 mb-2 dark:text-gray-300">
          Drag and drop files here, or{' '}
          <label className="text-primary-600 hover:text-primary-700 cursor-pointer underline dark:text-primary-400 dark:hover:text-primary-300">
            browse
            <input
              type="file"
              accept=".pdf,.txt,.md,.zip"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Supported: PDF, TXT, Markdown, ZIP (containing PDFs)
        </p>
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between dark:bg-gray-800/50 dark:border-gray-700">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} ready to upload
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedFiles([])}
                className="text-sm font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear all
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={14} />
                    Upload Files
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            {selectedFiles.map((file, index) => (
              <div key={index} className="px-4 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-center gap-2 min-w-0">
                  {getFileIcon(file)}
                  <span className="text-sm text-gray-700 truncate dark:text-gray-200">{file.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0 dark:text-gray-500">
                    {formatFileSize(file.size)}
                  </span>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="p-1 text-gray-400 transition-colors duration-150 hover:text-red-500 dark:hover:text-red-400"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
